import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Observable, interval, of, Subject, Subscription, tap } from 'rxjs';
import {
  EvaluationTranscriptService,
  CreateTranscriptRequest,
  UpdateTranscriptRequest,
  TranscriptResponse,
  ResponseMessage,
} from 'generated-api';
import { RecordingStopReason, SpeechRecognitionService, SpeechTranscriptSegment } from './speech-recognition.service';

export type TranscriptSegmentForSave = {
  speakerLabel: string;
  speakerId: string;
  text: string;
  recordedAt?: string;
};

export type UnexpectedStopEvent = {
  saved: boolean;
  capturedTranscript?: TranscriptSegmentForSave[];
  pending?: boolean;
  reason?: RecordingStopReason;
};

export function sharedAudioCaptureStoppedMessage(saved: boolean, hasTranscript: boolean): string {
  if (saved) {
    return 'The shared tab or window was refreshed or closed, so audio capture stopped. Recording has stopped, and your transcript up to that point was saved. Share the tab again and click "Continue recording" to resume.';
  }

  if (hasTranscript) {
    return 'The shared tab or window was refreshed or closed, so audio capture stopped. Recording has stopped. Your transcript is shown below, but it could not be saved to the server. Please try saving again or start a new recording.';
  }

  return 'The shared tab or window was refreshed or closed, so audio capture stopped. Recording has stopped before any transcript was saved. Please start a new recording.';
}

export type StartRecordingOptions = {
  fileName?: string;
  name?: string;
  existingFileId?: string;
  existingTranscript?: TranscriptSegmentForSave[];
};
import { v4 as uuidv4 } from 'uuid';
import { PosthogService } from './posthog.service';
import {
  estimateAzureSpeechCostUsd,
  ReverbAnalyticsEvent,
} from '../analytics/reverb-analytics.events';

@Injectable({
  providedIn: 'root',
})
export class TranscriptManagementService {
  private static readonly INCREMENTAL_SAVE_INTERVAL_MS = 5000;

  private evaluationTranscriptService = inject(EvaluationTranscriptService);
  private speechService = inject(SpeechRecognitionService);
  private posthogService = inject(PosthogService);
  private destroyRef = inject(DestroyRef);

  private readonly unexpectedStopSubject = new Subject<UnexpectedStopEvent>();
  readonly unexpectedStop$ = this.unexpectedStopSubject.asObservable();

  private readonly incrementalSaveResultSubject = new Subject<TranscriptResponse | ResponseMessage>();
  readonly incrementalSaveResult$ = this.incrementalSaveResultSubject.asObservable();

  private readonly incrementalSaveErrorSubject = new Subject<string>();
  readonly incrementalSaveError$ = this.incrementalSaveErrorSubject.asObservable();

  private readonly beforeUnloadHandler = (e: BeforeUnloadEvent) => {
    e.preventDefault();
  };

  currentRecordingId = signal<string | null>(null);
  isRecording = signal<boolean>(false);
  recordingMetadata = signal<{ patientId: string; evaluationTypeId: string; fileName?: string; name?: string } | null>(null);
  elapsedSeconds = signal<number>(0);
  formattedTime = computed(() => {
    const seconds = this.elapsedSeconds();
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  });
  currentRecordingName = computed(() => {
    return this.recordingMetadata()?.name || 'Recording in progress';
  });
  currentRecordingFileName = computed(() => {
    return this.recordingMetadata()?.fileName || 'Recording in progress';
  });

  private timerSubscription: Subscription | null = null;
  private incrementalSaveSubscription: Subscription | null = null;
  private incrementalSaveInFlight = false;
  private lastSavedSegmentFingerprint = '';
  private recordingNotes = signal('');
  readonly activeRecordingNotes = this.recordingNotes.asReadonly();
  private speakerUuidMap: { [key: string]: string } = {};
  private speakerDisplayNames: Record<string, string> = {};
  private baseTranscriptSegments: TranscriptSegmentForSave[] = [];
  private recordingStartedAtMs: number | null = null;

  constructor() {
    this.speechService.isRecording$
      .pipe(
        filter((recording) => !recording && this.isRecording()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.handleUnexpectedStop());

    this.speechService.error$
      .pipe(
        filter(() => this.isRecording()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.flushIncrementalSaveIfNeeded(true, { includeInterim: true });
      });
  }

  startRecording(patientId: string, evaluationTypeId: string, options?: StartRecordingOptions): void {
    const now = new Date();
    const defaultName = options?.name || `Recording ${this.formatDateForName(now)}`;
    const defaultFileName = options?.fileName || `Live Recording ${now.toLocaleDateString()}`;
    const existingTranscript = options?.existingTranscript ?? [];
    const continuing = Boolean(options?.existingFileId && options.existingFileId !== 'in-progress');

    this.recordingMetadata.set({
      patientId,
      evaluationTypeId,
      fileName: defaultFileName,
      name: defaultName,
    });
    this.isRecording.set(true);
    this.currentRecordingId.set(continuing ? options!.existingFileId! : null);
    this.baseTranscriptSegments = continuing
      ? existingTranscript.map((segment) => ({ ...segment }))
      : [];
    this.elapsedSeconds.set(0);
    this.recordingStartedAtMs = Date.now();
    this.seedSpeakerUuidMapFromTranscript(existingTranscript);

    this.stopTimer();
    this.timerSubscription = interval(1000).subscribe(() => {
      this.elapsedSeconds.update((v) => v + 1);
    });

    this.recordingNotes.set('');
    this.lastSavedSegmentFingerprint = '';
    this.startIncrementalAutoSave();

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  setRecordingNotes(notes: string): void {
    this.recordingNotes.set(notes);
  }

  updateRecordingFileName(fileName: string): void {
    const metadata = this.recordingMetadata();
    if (metadata) {
      this.recordingMetadata.set({ ...metadata, fileName });
    }
  }

  resolveSpeakerLabel(speakerUuid: string, fallbackLabel: string): string {
    return this.speakerDisplayNames[speakerUuid] || fallbackLabel;
  }

  setSpeakerDisplayName(speakerUuid: string, displayName: string): void {
    this.speakerDisplayNames[speakerUuid] = displayName;
  }

  updateBaseTranscriptSpeakerName(speakerUuid: string, displayName: string): void {
    this.setSpeakerDisplayName(speakerUuid, displayName);
    this.baseTranscriptSegments = this.baseTranscriptSegments.map((segment) =>
      segment.speakerId === speakerUuid ? { ...segment, speakerLabel: displayName } : segment
    );
  }

  replaceBaseTranscriptSegments(segments: TranscriptSegmentForSave[]): void {
    this.baseTranscriptSegments = segments.map((segment) => ({ ...segment }));
  }

  mergeSpeakers(sourceSpeakerId: string, targetSpeakerId: string, displayName: string): void {
    this.speakerDisplayNames[targetSpeakerId] = displayName;
    delete this.speakerDisplayNames[sourceSpeakerId];

    for (const key of Object.keys(this.speakerUuidMap)) {
      if (this.speakerUuidMap[key] === sourceSpeakerId) {
        this.speakerUuidMap[key] = targetSpeakerId;
      }
    }

    this.baseTranscriptSegments = this.baseTranscriptSegments.map((segment) => {
      if (segment.speakerId === sourceSpeakerId) {
        return { ...segment, speakerId: targetSpeakerId, speakerLabel: displayName };
      }
      if (segment.speakerId === targetSpeakerId) {
        return { ...segment, speakerLabel: displayName };
      }
      return segment;
    });
  }

  private formatDateForName(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  async stopRecording(options?: { fileName?: string; name?: string; notes?: string }): Promise<Observable<TranscriptResponse | ResponseMessage>> {
    const metadata = this.recordingMetadata();
    const durationSeconds = this.resolveRecordingDurationSeconds();

    this.stopTimer();
    this.stopIncrementalAutoSave();
    this.isRecording.set(false);
    this.removeBeforeUnloadListener();

    const segments = await this.speechService.stop();
    this.captureSpeechUsageAnalytics(metadata, durationSeconds);

    if (!segments.length && !this.currentRecordingId()) {
      throw new Error('No speech was detected');
    }

    if (!segments.length && this.currentRecordingId()) {
      return of({ status: true, guid: this.currentRecordingId()! } as ResponseMessage);
    }

    const transcriptData = this.buildTranscriptForSave(segments);
    const resolvedMetadata = metadata ?? this.recordingMetadata();

    if (!resolvedMetadata) {
      throw new Error('No recording metadata found');
    }

    const fileName = options?.fileName || resolvedMetadata.fileName || `Live Recording ${new Date().toLocaleDateString()}`;
    const transcriptName = options?.name || resolvedMetadata.name || `Interview ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;

    const fileId = this.currentRecordingId();

    if (fileId && fileId !== 'in-progress') {
      const request: UpdateTranscriptRequest = {
        fileId: fileId,
        evaluationTypeId: resolvedMetadata.evaluationTypeId,
        transcript: transcriptData.transcript,
        fileName,
        name: transcriptName,
        notes: options?.notes ?? this.recordingNotes(),
      };

      return this.evaluationTranscriptService.updateTranscript(request).pipe(
        tap((result) => {
          if (result.filePkid) {
            this.currentRecordingId.set(result.filePkid);
          }
        })
      );
    } else {
      const request: CreateTranscriptRequest = {
        patientId: resolvedMetadata.patientId,
        evaluationTypeId: resolvedMetadata.evaluationTypeId,
        transcript: transcriptData.transcript,
        fileName,
        name: transcriptName,
        notes: options?.notes ?? this.recordingNotes(),
      };

      return this.evaluationTranscriptService.createTranscript(request).pipe(
        tap((result) => {
          if (result.status && result.guid) {
            this.currentRecordingId.set(result.guid);
          }
        })
      );
    }
  }

  saveIncrementalTranscript(segments: SpeechTranscriptSegment[], notes?: string): Observable<TranscriptResponse | ResponseMessage> {
    const transcriptData = this.buildTranscriptForSave(segments);
    if (!transcriptData.transcript.length) {
      throw new Error('No segments to save');
    }

    const request = this.buildIncrementalSaveRequest(segments, notes);
    if (!request) {
      throw new Error('No recording metadata found');
    }

    if (request.update) {
      return this.evaluationTranscriptService.updateTranscript(request.update).pipe(
        tap((result) => {
          if (result.filePkid) {
            this.currentRecordingId.set(result.filePkid);
          }
        })
      );
    }

    return this.evaluationTranscriptService.createTranscript(request.create!).pipe(
      tap((result) => {
        if (result.status && result.guid) {
          this.currentRecordingId.set(result.guid);
        }
      })
    );
  }

  flushIncrementalSaveIfNeeded(
    force = false,
    options?: { includeInterim?: boolean }
  ): void {
    if (!this.isRecording() || this.incrementalSaveInFlight) {
      return;
    }

    const segments = this.collectSpeechSegmentsForSave(options?.includeInterim ?? false);
    if (!segments.length) {
      return;
    }

    const fingerprint = this.getSegmentFingerprint(segments);
    if (!force && fingerprint === this.lastSavedSegmentFingerprint && this.currentRecordingId()) {
      return;
    }

    this.incrementalSaveInFlight = true;
    this.saveIncrementalTranscript(segments, this.recordingNotes()).subscribe({
      next: (result) => {
        this.lastSavedSegmentFingerprint = fingerprint;
        this.incrementalSaveInFlight = false;
        this.incrementalSaveResultSubject.next(result);
      },
      error: (err) => {
        this.incrementalSaveInFlight = false;
        console.error('Failed to save incremental transcript', err);
        this.incrementalSaveErrorSubject.next(
          err?.message || 'Failed to save transcript. Your recording is still active.'
        );
      },
    });
  }

  private seedSpeakerUuidMapFromTranscript(transcript: TranscriptSegmentForSave[]): void {
    this.speakerUuidMap = {};
    this.speakerDisplayNames = {};
    for (const segment of transcript) {
      const speakerLabel = segment.speakerLabel || '';
      const speakerId = segment.speakerId || '';
      if (speakerLabel && speakerId) {
        this.speakerUuidMap[speakerLabel] = speakerId;
        this.speakerDisplayNames[speakerId] = speakerLabel;
      }
    }
  }

  mergeSpeechSegmentsWithBase(segments: SpeechTranscriptSegment[]): TranscriptSegmentForSave[] {
    return this.buildTranscriptForSave(segments).transcript;
  }

  private buildTranscriptForSave(segments: SpeechTranscriptSegment[]): {
    transcript: TranscriptSegmentForSave[];
  } {
    const newSegments = this.formatSegmentsForSave(segments).transcript;
    return { transcript: this.mergeTranscriptSegments(this.baseTranscriptSegments, newSegments) };
  }

  private mergeTranscriptSegments(
    base: TranscriptSegmentForSave[],
    appended: TranscriptSegmentForSave[]
  ): TranscriptSegmentForSave[] {
    if (!base.length) {
      return appended.map((segment) => ({ ...segment }));
    }
    if (!appended.length) {
      return base.map((segment) => ({ ...segment }));
    }

    // Always append continued recording as new rows, even when the same guest speaks again.
    return [
      ...base.map((segment) => ({ ...segment })),
      ...appended.map((segment) => ({ ...segment })),
    ];
  }

  private formatSegmentsForSave(segments: SpeechTranscriptSegment[]): { transcript: TranscriptSegmentForSave[] } {
    const transcript: TranscriptSegmentForSave[] = [];

    for (const segment of segments) {
      const speakerLabel = segment.speakerId;
      if (!this.speakerUuidMap[speakerLabel]) {
        this.speakerUuidMap[speakerLabel] = uuidv4();
      }

      const speakerId = this.speakerUuidMap[speakerLabel];

      const last = transcript[transcript.length - 1];
      if (last && last.speakerId === speakerId) {
        last.text += ' ' + segment.text;
      } else {
        transcript.push({
          speakerLabel: this.resolveSpeakerLabel(speakerId, speakerLabel),
          speakerId,
          text: segment.text,
          recordedAt: segment.recordedAt ?? new Date().toISOString(),
        });
      }
    }

    return { transcript };
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private startIncrementalAutoSave(): void {
    this.stopIncrementalAutoSave();
    this.incrementalSaveSubscription = interval(TranscriptManagementService.INCREMENTAL_SAVE_INTERVAL_MS).subscribe(() => {
      this.flushIncrementalSaveIfNeeded();
    });
  }

  private stopIncrementalAutoSave(): void {
    if (this.incrementalSaveSubscription) {
      this.incrementalSaveSubscription.unsubscribe();
      this.incrementalSaveSubscription = null;
    }
  }

  private getSegmentFingerprint(segments: SpeechTranscriptSegment[]): string {
    return segments.map((segment) => `${segment.speakerId}:${segment.text}`).join('|');
  }

  private buildIncrementalSaveRequest(
    segments: SpeechTranscriptSegment[],
    notes?: string
  ): { create?: CreateTranscriptRequest; update?: UpdateTranscriptRequest } | null {
    const transcriptData = this.buildTranscriptForSave(segments);
    const metadata = this.recordingMetadata();
    if (!metadata) {
      return null;
    }

    const resolvedNotes = notes ?? this.recordingNotes();
    const fileId = this.currentRecordingId();

    if (fileId && fileId !== 'in-progress') {
      return {
        update: {
          fileId,
          evaluationTypeId: metadata.evaluationTypeId,
          transcript: transcriptData.transcript.map((segment) => ({
            speakerLabel: segment.speakerLabel || '',
            speakerId: segment.speakerId || '',
            text: segment.text || '',
            recordedAt: segment.recordedAt,
          })),
          notes: resolvedNotes,
          fileName: metadata.fileName,
          name: metadata.name,
        },
      };
    }

    const now = new Date();
    const fileName = metadata.fileName || `Live Recording ${now.toLocaleDateString()}`;
    const transcriptName = metadata.name || `Interview ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    return {
      create: {
        patientId: metadata.patientId,
        evaluationTypeId: metadata.evaluationTypeId,
        transcript: transcriptData.transcript,
        notes: resolvedNotes,
        fileName,
        name: transcriptName,
      },
    };
  }

  private removeBeforeUnloadListener(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private handleUnexpectedStop(): void {
    const notes = this.recordingNotes();
    const segments = this.collectSpeechSegmentsForSave(true);
    const transcriptData = this.buildTranscriptForSave(segments);
    const capturedTranscript = transcriptData.transcript.length
      ? transcriptData.transcript
      : undefined;
    const reason = this.speechService.takeUnexpectedStopReason() ?? undefined;

    this.stopTimer();
    this.stopIncrementalAutoSave();
    this.isRecording.set(false);
    this.removeBeforeUnloadListener();

    if (!capturedTranscript?.length) {
      this.unexpectedStopSubject.next({ saved: false, reason });
      return;
    }

    this.unexpectedStopSubject.next({
      saved: false,
      capturedTranscript,
      pending: true,
      reason,
    });

    this.incrementalSaveInFlight = true;
    this.saveIncrementalTranscript(segments, notes).subscribe({
      next: () => {
        this.lastSavedSegmentFingerprint = this.getSegmentFingerprint(segments);
        this.incrementalSaveInFlight = false;
        this.incrementalSaveResultSubject.next({ status: true } as ResponseMessage);
        this.unexpectedStopSubject.next({ saved: true, capturedTranscript, reason });
      },
      error: (err) => {
        this.incrementalSaveInFlight = false;
        console.error('Failed to save transcript after recording stopped unexpectedly', err);
        this.unexpectedStopSubject.next({ saved: false, capturedTranscript, reason });
      },
    });
  }

  private collectSpeechSegmentsForSave(includeInterim: boolean): SpeechTranscriptSegment[] {
    const segments = [...this.speechService.finalText()];
    if (!includeInterim) {
      return segments;
    }

    const interim = this.speechService.interimText();
    const interimText = interim.text?.trim();
    if (!interimText) {
      return segments;
    }

    return [
      ...segments,
      {
        text: interimText,
        speakerId: interim.speakerId || 'Unknown',
        recordedAt: new Date().toISOString(),
      },
    ];
  }

  async cancelActiveRecording(): Promise<void> {
    const metadata = this.recordingMetadata();
    const durationSeconds = this.resolveRecordingDurationSeconds();

    this.stopTimer();
    this.stopIncrementalAutoSave();
    this.isRecording.set(false);
    this.removeBeforeUnloadListener();

    try {
      await this.speechService.stop();
    } catch {
      // Microphone may already be stopped.
    }

    this.captureSpeechUsageAnalytics(metadata, durationSeconds, 'cancelled');

    this.reset();
    this.speechService.reset();
  }

  private resolveRecordingDurationSeconds(): number {
    if (this.recordingStartedAtMs != null) {
      const elapsedMs = Date.now() - this.recordingStartedAtMs;
      if (elapsedMs > 0) {
        return Math.max(this.elapsedSeconds(), Math.ceil(elapsedMs / 1000));
      }
    }
    return this.elapsedSeconds();
  }

  private captureSpeechUsageAnalytics(
    metadata: { patientId: string; evaluationTypeId: string; fileName?: string; name?: string } | null,
    durationSeconds: number,
    source = 'interview_recording'
  ): void {
    if (durationSeconds <= 0) {
      return;
    }

    const patientId = metadata?.patientId?.trim();
    const evaluationTypeId = metadata?.evaluationTypeId?.trim();
    this.posthogService.captureReverb(ReverbAnalyticsEvent.azureSpeechUsage, {
      ...this.posthogService.practiceEventProperties(),
      audio_duration_seconds: durationSeconds,
      usage_type: 'conversation_transcription',
      include_diarization: true,
      estimated_cost_usd: estimateAzureSpeechCostUsd(durationSeconds, true),
      service: 'azure_speech',
      cost_explorer_service_line: 'azureSpeech',
      source,
      ...(patientId
        ? { patient_id: patientId, client_id: patientId }
        : {}),
      ...(evaluationTypeId ? { evaluation_type_id: evaluationTypeId } : {}),
    });
  }

  reset(): void {
    this.stopTimer();
    this.stopIncrementalAutoSave();
    this.removeBeforeUnloadListener();
    this.currentRecordingId.set(null);
    this.isRecording.set(false);
    this.recordingMetadata.set(null);
    this.recordingNotes.set('');
    this.lastSavedSegmentFingerprint = '';
    this.incrementalSaveInFlight = false;
    this.recordingStartedAtMs = null;
    this.elapsedSeconds.set(0);
    this.speakerUuidMap = {};
    this.speakerDisplayNames = {};
    this.baseTranscriptSegments = [];
  }
}
